import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JSONParser } from '@streamparser/json';
import {
  ApiErrorResponse,
  getStorageValue,
  KEY_MYGPT_TOKEN,
  KEY_USER_FINGERPRINT,
  KEY_USER_NICKNAME,
  questions as questionsRequest,
  useQuestionSuggestion,
  getBrowserFingerprint,
} from '@mygpt/app';
import {
  ClarificationType,
  ERROR_TYPE,
  FaqChildNode,
  Footnote,
  PageInfo,
  QuestionType,
  RecommendList,
  ReferenceContent,
  ReferenceSign,
  SessionListItem,
  SessionListParam,
} from '@mygpt/types';

import { QUESTION_TYPE_CHAT, QuestionResponseFormat } from '../constant';
import { getWidgetToken, isWidgetPluginPath } from '@mygpt/app/apis/widgetToken';

const QUESTION_TYPE_SIMPLE = QUESTION_TYPE_CHAT.SIMPLE as QuestionType;

interface Props {
  aiId: string;
  sessionId: string;
  questionType?: QuestionType;
  messageId: string;
  showPrompt?: boolean;
  // 记录问答时当前页面信息
  pageInfo?: PageInfo;
  language?: string;
  agentMode?: string;
  userIdentifier?: string;
  isLocalMode?: boolean;
}

const splitSource = (text: string) => {
  const splitWord = '$SOURCES$:';
  const splitArr = text.split(splitWord);
  const oriText = splitArr[0];
  let sourceArr = (splitArr[1] || '').split(',');
  sourceArr = sourceArr.map((item) => item.trim()).filter((item) => item !== '');
  return {
    oriText,
    sourceArr,
  };
};

enum StreamingMessageDataType {
  REFERENCE_LIST = 'REFERENCE_LIST',
  REFERENCE_SIGN = 'REFERENCE_SIGN',
  REFERENCE = 'REFERENCE',
  PLAIN_TEXT = 'PLAIN_TEXT',
  FINISH = 'FINISH',
  ERROR = 'ERROR',
  LLM_CAN_ANSWER = 'LLM_CAN_ANSWER',
  FAQ_CHILD_NODE = 'FAQ_CHILD_NODE', // FAQ 问题树
  RECOMMEND_LIST = 'RECOMMEND_LIST', // 相关问题推荐
  SHOW_REFERENCE_BUTTON = 'SHOW_REFERENCE_BUTTON', // 展示引用按钮
  CLARIFICATION = 'CLARIFICATION', // 澄清类型。
}
interface MessageResponse {
  message_type: StreamingMessageDataType;
  message_id: string;
  content: unknown;
  use_faq: boolean;
  tokens: number;
}

interface Reference extends Footnote {}

export interface FetchQuestionOptions {
  faqID?: string;
  postAnswerProcessing?: (question: string, message: SessionListParam) => void;
  displayText?: string; // 用户可见文本（实际发给 AI 的是 value 参数）
}

const QuestionMessageErrorPrefix = 'Message_Error_';

export const useQuestion = ({
  aiId: id,
  messageId,
  pageInfo,
  questionType = QUESTION_TYPE_CHAT.SIMPLE as QuestionType,
  sessionId,
  showPrompt,
  language,
  agentMode,
  userIdentifier,
  isLocalMode,
}: Props) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [sessionList, setSessionList] = useState<SessionListItem>({ messages: [] });

  const controllerRef = useRef<AbortController>();
  const forcedPauseRef = useRef(false);
  const questionHasRsp = useRef(false);
  const traceIdRef = useRef<string | undefined>();

  const isWidgetPluginPage = useMemo(() => isWidgetPluginPath(), []);

  const logAnswerError = (msg: unknown) => {
    console.log('logAnswerError', msg);
    Sentry.captureMessage(`Chatbot回答异常 botId:${id}, info:${msg}`, 'error');
  };

  const pendingToAnswer = (postAnswerProcessing?: (question: string, message: SessionListParam) => void) => {
    setSessionList((state: SessionListItem) => {
      const text = state.pending ?? '';
      const { oriText, sourceArr } = splitSource(text);

      const message: SessionListParam = {
        isAnswer: true,
        text: oriText,
        footnotes: state.footnotes,
        reference_list: state.reference_list,
        reference_indexes: state.reference_indexes,
        recommend_list: state.recommend_list,
        faq_child_node: state.faq_child_node,
        source: sourceArr,
        messageId,
        llm_can_answer: state.llm_can_answer,
        clarification: state.clarification,
        traceId: traceIdRef.current,
      };

      if (forcedPauseRef.current) {
        message.userAborted = true;
      }

      const question = state.messages.at(-1)?.text;
      if (question) {
        // 调用函数，进行一些后置处理逻辑
        postAnswerProcessing?.(question, message);
      }

      return {
        messages: [...state.messages, message],
        reference_list: state.reference_list,
        reference_indexes: undefined,
        footnotes: undefined,
        pending: undefined,
      };
    });
  };

  const tokenIsExpired = (): boolean => {
    const token = (Cookies.get(KEY_MYGPT_TOKEN()) || '').replace('Bearer ', '');
    if (!token) return true;
    const tokenDecoded = jwtDecode(token);
    if (!tokenDecoded || !tokenDecoded.exp) return true;
    return dayjs(tokenDecoded.exp * 1000).isBefore(dayjs());
  };

  const stopAnswer = () => {
    forcedPauseRef.current = true;

    if (!questionHasRsp.current) {
      controllerRef.current && controllerRef.current.abort('User Canceled');
    }
  };

  const fetchQuestion = async (
    value: string,
    { faqID, postAnswerProcessing, displayText }: FetchQuestionOptions = {}
  ) => {
    setSessionList((state: SessionListItem) => ({
      ...state,
      messages: [
        ...state.messages,
        {
          isAnswer: false,
          text: displayText || value,
        },
      ],
    }));

    if (!id) return;

    controllerRef.current = new AbortController();
    forcedPauseRef.current = false;

    const HISMobilePhoneList = (window as any).hismobile_phone_list;
    const validPhoneList = Array.isArray(HISMobilePhoneList) && HISMobilePhoneList?.length;

    const dynamicBackground = [];
    if (validPhoneList) {
      dynamicBackground.push({
        description: `The correspondence between the user's phone number and the encrypted information is represented by the variables phone and encrypted_phone. phone represents the user's real phone number, while encrypted_phone represents the encrypted string of the user's real phone number.`,
        phone_mapper: HISMobilePhoneList,
      });
    }

    // 获取最新的搜索建议
    const currentQuestionSuggestions = useQuestionSuggestion.getState().questionSuggestions;
    // OnPrem 模式下，不需要用户指纹，固定展示成：访客，即可
    const backupUserIdentifier = isLocalMode ? 'guest' : getStorageValue(KEY_USER_FINGERPRINT);

    // 获取 user_identifier，如果所有存储都为空，则动态生成 fingerprint 作为兜底
    let finalUserIdentifier =
      userIdentifier || getStorageValue(KEY_USER_NICKNAME) || getStorageValue(KEY_USER_FINGERPRINT);

    if (!finalUserIdentifier) {
      finalUserIdentifier = (await getBrowserFingerprint()) || '';
    }

    const param = {
      ai_id: id,
      session_id: sessionId,
      message_id: messageId,
      question: value,
      stream: !showPrompt,
      debug_with_messages: showPrompt,
      simple_question: questionType === QUESTION_TYPE_SIMPLE,
      with_source: true,
      // 小部件默认匿名；只有用户在小部件内登录过（sessionStorage 有 token）才带登录态
      needToken: isWidgetPluginPage ? !!getWidgetToken() : !tokenIsExpired(),
      format: QuestionResponseFormat.JSON_AST,
      user_identifier: finalUserIdentifier,
      tool_response: true, // 对于 agent 类型的 bot，返回 tool call 和 tool response 详情
      ...(dynamicBackground.length ? { dynamic_background: dynamicBackground } : {}),
    };
    // 大屏模式才需要指定语言及模式
    if (language) Object.assign(param, { language });
    if (agentMode) Object.assign(param, { agent_mode: agentMode });

    if (faqID) Object.assign(param, { recommend_faq_id: faqID });
    if (pageInfo && (pageInfo.title || pageInfo.url)) Object.assign(param, { page_info: pageInfo });
    if (currentQuestionSuggestions && currentQuestionSuggestions.length > 0)
      Object.assign(param, {
        metadata: currentQuestionSuggestions.map((item) => ({
          key: item.id,
          value: item.question,
          type: 'FAQ_QUESTION',
        })),
      });
    questionHasRsp.current = false;
    questionsRequest(param, controllerRef.current.signal)
      .then(async (response) => {
        // 捕获 x-request-id 用于追踪
        const reqTraceId = response.headers.get('x-request-id');
        if (reqTraceId) traceIdRef.current = reqTraceId;

        const parser = new JSONParser({
          separator: '\n', // 以换行符作为分隔符来尝试解析 JSON
        });

        if (!response.body) {
          setLoading(false);
          return;
        }

        const decoder = new TextDecoder();
        const reader = response.body.getReader();
        questionHasRsp.current = true;

        // 测试环境功能
        if (showPrompt) {
          let responseVal = '';
          // eslint-disable-next-line
          while (true) {
            const { done, value } = await reader.read();
            if (done || forcedPauseRef.current) {
              const answerData = JSON.parse(responseVal);
              setSessionList((state: SessionListItem) => ({
                messages: [
                  ...state.messages,
                  { isAnswer: true, text: answerData.answer, prompt: answerData.messages[0]?.content },
                ],
                pending: undefined,
              }));
              break;
            }

            const text = decoder.decode(value, { stream: true });
            responseVal += text;
          }
        }

        const recommendList: RecommendList[] = [];
        const faqChildNode: FaqChildNode[] = [];
        let llm_can_answer: boolean = true;
        let clarification: ClarificationType | undefined;
        const referenceIndexes: ReferenceSign[] = [];

        parser.onValue = ({ value: json, key }) => {
          // 如果有key, 说明解析到的是一个不完整的JSON的一部分，这种数据不处理，继续等待
          // 如果key为空，说明解析到的是一个完整的、解析好的JSON，可以直接使用。
          if (key !== undefined) return;

          let message: MessageResponse = {
            message_type: StreamingMessageDataType.PLAIN_TEXT,
          } as MessageResponse;

          message = json as any;

          const referenceList: Reference[] = [];

          if (message.message_type === StreamingMessageDataType.REFERENCE_LIST) {
            if (message.content) {
              for (const item of message.content as Reference[]) {
                if (item.source) {
                  referenceList.push(item);
                  // Generate reference indexes for agent mode to enable citations
                  referenceIndexes.push({
                    id: item.id,
                    title: item.title,
                    sentences: [], // Will be populated by getReference if needed
                  });
                }
              }
            }
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.SHOW_REFERENCE_BUTTON) {
            if (message.content !== 'true' && !message.use_faq) {
              referenceList.splice(0, referenceList.length);
              referenceIndexes.splice(0, referenceIndexes.length);
            }
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.LLM_CAN_ANSWER) {
            llm_can_answer = message.content === 'true' ? true : false;
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.RECOMMEND_LIST) {
            if (message.content && Array.isArray(message.content) && message.content.length > 0) {
              recommendList.push(...(message.content as Array<RecommendList>));
            }
            // 清空 message.content 以便后续 setSessionList 能够正常执行
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.FAQ_CHILD_NODE) {
            if (message.content && Array.isArray(message.content) && message.content.length > 0) {
              faqChildNode.push(...(message.content as Array<FaqChildNode>));
            }
            // 清空 message.content 以便后续 setSessionList 能够正常执行
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.ERROR) {
            throw new Error(`${QuestionMessageErrorPrefix}${message.content}`);
          } else if (message.message_type === StreamingMessageDataType.CLARIFICATION) {
            clarification = message.content as ClarificationType;
            // 情况内容，是为了走下面的 setSessionList 逻辑时，不会把值也显示出来
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.REFERENCE) {
            if (!message.content) return;
            // Convert REFERENCE content to a special marker that can be parsed in the UI
            const refContent = message.content as ReferenceContent;
            const refMarker = `<ref_data>${JSON.stringify(refContent)}</ref_data>`;
            message.content = refMarker;
          } else if (message.message_type !== StreamingMessageDataType.PLAIN_TEXT) {
            // 兜底：未识别的非 PLAIN_TEXT 控制事件不应把 content 拼进答案，
            // 防止后端新增 message_type 时（例如 SHOW_REFERENCE_BUTTON / LLM_CAN_ANSWER 这类布尔控制事件）
            // 把 "true"/"false" 这类元数据值泄漏到对话内容里。
            message.content = '';
          }

          setSessionList((state: SessionListItem) => {
            const footnotes = state.footnotes ?? [];

            const pending = (state.pending ?? '') + message.content;

            const newState = {
              ...state,
              footnotes,
              reference_list: (state.reference_list || [])?.concat(referenceList),
              reference_indexes: referenceIndexes,
              recommend_list: recommendList,
              faq_child_node: faqChildNode,
              pending: pending,
              llm_can_answer,
              clarification,
            };

            return newState;
          });
        };

        if (!showPrompt) {
          // eslint-disable-next-line
          while (true) {
            const { done, value } = await reader.read();
            if (done || forcedPauseRef.current) {
              pendingToAnswer(postAnswerProcessing);
              setLoading(false);
              controllerRef.current && controllerRef.current.abort();
              break;
            }

            let text = decoder.decode(value, { stream: true });
            try {
              parser.write(text);
            } catch (err) {
              console.log(`[useQuestion] 解析JSON失败: ${err}`); // handler errors
            }
          }
        }
      })
      .catch((err: ApiErrorResponse) => {
        let errorType = ERROR_TYPE.UNKNOWN_ERROR;
        const error = (typeof err.errorMsg === 'string' ? new Error(err.errorMsg) : err.errorMsg) as Error;
        let errorMsg: string | undefined = err.errorMsg ?? '';
        console.log('err', err);

        // 首先判断是否是某种权限错误
        // 再判断是否是否是用户取消
        // 再判断是否是已知的接口错误
        // 最后都当成网络错误
        if ([401, 402].includes(err.statusCode)) {
          errorType = ERROR_TYPE.PERMISSION_LIMIT;
        } else if (errorMsg?.includes('user canceled')) {
          errorType = ERROR_TYPE.USER_CANCELED;
        } else if (error?.message) {
          errorMsg = t('errors.answer.description');
          errorType = ERROR_TYPE.API_ERROR;
        } else {
          errorMsg = t('errors.network.description');
          errorType = ERROR_TYPE.NETWORK_ERROR;
        }

        logAnswerError(`代码判断的错误类型：${errorType}，原始错误信息：${errorMsg}`);
        setSessionList((state: SessionListItem) => ({
          messages: [
            ...state.messages,
            {
              isAnswer: true,
              text: '',
              prompt: '',
              error: errorMsg,
              error_type: errorType,
              status: err.statusCode,
              question: value,
            },
          ],
          footnotes: undefined,
          recommend_list: undefined,
          reference_list: undefined,
          reference_indexes: undefined,
          faq_child_node: undefined,
          pending: undefined,
          llm_can_answer: undefined,
          clarification: undefined,
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearSessionList = () => {
    setSessionList({ messages: [] });
  };

  const updateSessionList = (state: SessionListItem) => {
    setSessionList(() => ({
      messages: state.messages,
      footnotes: state.footnotes,
      recommend_list: state.recommend_list,
      reference_list: state.reference_list,
      faq_child_node: state.faq_child_node,
      reference_indexes: state.reference_indexes,
      pending: state.pending,
      llm_can_answer: state.llm_can_answer,
      clarification: state.clarification,
    }));
  };

  return { loading, setLoading, sessionList, fetchQuestion, clearSessionList, updateSessionList, stopAnswer };
};
